import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Users,
  Plus,
  Send,
  Search,
  Loader2,
  AlertCircle,
  Check,
  Clock,
  ListPlus,
  Trash2,
  Crown,
  Mail,
  CreditCard,
  Gift,
  Sparkles,
  ShieldCheck,
  HelpCircle,
  Filter,
} from "lucide-react";
import { toast } from "sonner";
import MemberBadges from "./MemberBadges";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * MembersPanel — full member directory + invite controls + Pro source.
 *
 * Drop into AdminHome (currently the front page of /admin) so the
 * first thing the founder sees is who just signed up. Keeps the
 * single + bulk invite forms above the table, since the workflow
 * "follow up with someone you met IRL" still matters even with the
 * beta gate disabled.
 *
 * Status badges:
 *   • PRO (paid)    — Stripe checkout completed; real revenue
 *   • PRO (gift)    — gift-code redemption
 *   • PRO (manual)  — admin-comp granted from this panel
 *   • PRO (admin)   — DEV_EMAILS allowlist (founder / dev accounts)
 *   • PRO (review)  — active Pro but missing source attribution
 *                     (pre-migration users; one click here to fix)
 *   • Member        — signed up, free tier
 *   • Invited       — got an email invite, hasn't signed up yet
 *   • Allowlisted   — admin-added but no email sent
 */
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const fmtRelative = (iso) => {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    const diff = (Date.now() - dt.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
    return fmtDate(iso);
  } catch {
    return "—";
  }
};

const PROVIDER_LABEL = {
  google: "Google",
  password: "Password",
  magic_link: "Magic link",
  stripe_checkout: "Stripe Checkout",
};

const PLAN_LABEL = {
  pro_monthly: "Monthly Pro",
  pro_annual: "Annual Pro",
  founding_lifetime: "Founding Lifetime",
};

const trialConversionLabel = (member) => {
  if (!member || member.kind !== "member") return null;
  if (["manual", "gift_code", "admin"].includes(member.pro_source)) {
    return {
      primary: "No charge",
      secondary:
        member.pro_source === "gift_code"
          ? "Gifted Pro access"
          : "Complimentary Pro access",
      className: "text-white/55",
    };
  }
  if (!member.trial_ends_at && member.selected_package_id) {
    return {
      primary:
        member.checkout_status === "expired"
          ? "Checkout expired"
          : "Checkout not completed",
      secondary: "No charge scheduled yet",
      className: "text-[#D4B483]",
    };
  }
  if (!member.trial_ends_at) {
    return {
      primary: "Not scheduled",
      secondary: "No paid plan selected",
      className: "text-white/35",
    };
  }
  if (
    member.trial_canceled_before_payment ||
    (member.subscription_status === "trialing" &&
      member.subscription_cancel_at_period_end)
  ) {
    return {
      primary: "Won't convert",
      secondary: "Canceled before charge",
      className: "text-[#FBA9A1]",
    };
  }
  if (member.first_paid_at || member.trial_converted_at) {
    return {
      primary: fmtDate(member.first_paid_at || member.trial_converted_at),
      secondary: "Converted to paid",
      className: "text-[#84C99A]",
    };
  }
  if (member.subscription_status === "trialing") {
    return {
      primary: fmtDate(member.trial_ends_at),
      secondary: "Scheduled first charge",
      className: "text-[#D4B483]",
    };
  }
  if (member.subscription_status === "canceled") {
    return {
      primary: "Canceled",
      secondary: "No first charge recorded",
      className: "text-white/45",
    };
  }
  return {
    primary: fmtDate(member.trial_ends_at),
    secondary: "Trial end",
    className: "text-white/55",
  };
};

// Pro source → visual config (icon, label, chip colours).
// Kept inline rather than as a Tailwind class lookup so the colour
// palette stays grep-able and reviewers can audit it in one place.
const PRO_SOURCE_META = {
  paid: {
    label: "Paid",
    icon: CreditCard,
    chipClass:
      "bg-[#0E3724]/60 text-[#86EFAC] border-[#86EFAC]/40",
    explainer: "Stripe checkout — real revenue.",
  },
  gift_code: {
    label: "Gift",
    icon: Gift,
    chipClass:
      "bg-[#3a1e3a]/60 text-[#F9A8D4] border-[#F9A8D4]/40",
    explainer: "Redeemed a gift code.",
  },
  manual: {
    label: "Manual",
    icon: Sparkles,
    chipClass:
      "bg-[#28324A]/60 text-[#93C5FD] border-[#93C5FD]/40",
    explainer: "Admin-granted comp.",
  },
  admin: {
    label: "Admin",
    icon: ShieldCheck,
    chipClass:
      "bg-[#3a2a08]/60 text-[#D4A14A] border-[#D4A14A]/40",
    explainer: "Founder / dev account (DEV_EMAILS).",
  },
  unknown: {
    label: "Review",
    icon: HelpCircle,
    chipClass:
      "bg-white/8 text-white/65 border-white/20",
    explainer: "Pro active but source not recorded.",
  },
};

const FILTER_TABS = [
  { key: "", label: "All" },
  { key: "pro", label: "All Pro" },
  { key: "paid", label: "Paid" },
  { key: "gift", label: "Gift" },
  { key: "manual", label: "Manual" },
  { key: "admin", label: "Admin" },
  { key: "unknown", label: "Review" },
  { key: "trial", label: "Trials" },
  { key: "free", label: "Free" },
];

const MembersPanel = () => {
  const [data, setData] = useState({
    rows: [],
    total_members: 0,
    total_invitees: 0,
    total_pro: 0,
    pro_by_source: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("");

  // Single-add form
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newNote, setNewNote] = useState("");
  const [sendInvite, setSendInvite] = useState(true);

  // Bulk-add form
  const [mode, setMode] = useState("single"); // "single" | "bulk"
  const [bulkRaw, setBulkRaw] = useState("");
  const [bulkNote, setBulkNote] = useState("");
  const [bulkSend, setBulkSend] = useState(true);
  const [bulking, setBulking] = useState(false);

  // Per-row resend state — track which rows are currently sending
  const [resendingId, setResendingId] = useState(null);

  // Manual-grant modal state
  const [grantingFor, setGrantingFor] = useState(null); // member row
  const [grantMonths, setGrantMonths] = useState(1);
  const [grantNote, setGrantNote] = useState("");
  const [granting, setGranting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: payload } = await axios.get(`${API}/admin/members`, {
        params: filter ? { filter } : {},
        withCredentials: true,
      });
      setData(payload || { rows: [] });
    } catch (err) {
      setError(
        err?.response?.status === 403
          ? "Admins only — sign in as an admin."
          : "Couldn't load members.",
      );
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const onAddSingle = async (e) => {
    e?.preventDefault?.();
    if (!newEmail.trim() || adding) return;
    setAdding(true);
    try {
      await axios.post(
        `${API}/admin/beta-allowlist`,
        {
          email: newEmail.trim().toLowerCase(),
          note: newNote.trim() || null,
          send_invite: sendInvite,
        },
        { withCredentials: true },
      );
      setNewEmail("");
      setNewNote("");
      const sentNote = sendInvite
        ? "Invite email sent."
        : "Added to invite list (no email).";
      toast.success(sentNote);
      await load();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Couldn't add — check the email and try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't add.");
    } finally {
      setAdding(false);
    }
  };

  const onAddBulk = async (e) => {
    e?.preventDefault?.();
    if (!bulkRaw.trim() || bulking) return;
    setBulking(true);
    try {
      const { data: out } = await axios.post(
        `${API}/admin/beta-allowlist/bulk`,
        {
          emails_raw: bulkRaw,
          note: bulkNote.trim() || null,
          send_invite: bulkSend,
        },
        { withCredentials: true },
      );
      setBulkRaw("");
      setBulkNote("");
      const parts = [
        `${out.added} added`,
        out.skipped ? `${out.skipped} skipped` : null,
        bulkSend ? `${out.invited} invites sent` : null,
        bulkSend && out.invite_failures
          ? `${out.invite_failures} failures`
          : null,
      ].filter(Boolean);
      toast.success(parts.join(" · "));
      await load();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Couldn't process the bulk add.";
      toast.error(typeof msg === "string" ? msg : "Couldn't process.");
    } finally {
      setBulking(false);
    }
  };

  const onResend = async (row) => {
    if (resendingId || !row.allowlist_id) return;
    setResendingId(row.allowlist_id);
    try {
      await axios.post(
        `${API}/admin/beta-allowlist/${row.allowlist_id}/resend`,
        {},
        { withCredentials: true },
      );
      toast.success(`Invite resent to ${row.email}.`);
      await load();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Couldn't resend — try again.";
      toast.error(typeof msg === "string" ? msg : "Resend failed.");
    } finally {
      setResendingId(null);
    }
  };

  const onRemoveInvitee = async (row) => {
    if (!row.allowlist_id) return;
    if (
      !window.confirm(
        `Remove ${row.email} from your invite list?`,
      )
    )
      return;
    try {
      await axios.delete(
        `${API}/admin/beta-allowlist/${row.allowlist_id}`,
        { withCredentials: true },
      );
      toast.success(`Removed ${row.email}.`);
      await load();
    } catch {
      toast.error("Couldn't remove. Try again.");
    }
  };

  const openGrant = (row) => {
    setGrantingFor(row);
    setGrantMonths(1);
    setGrantNote("");
  };

  const closeGrant = () => {
    if (granting) return;
    setGrantingFor(null);
  };

  const onSubmitGrant = async (e) => {
    e?.preventDefault?.();
    if (!grantingFor || granting) return;
    const months = parseInt(grantMonths, 10);
    if (!Number.isFinite(months) || months < 1 || months > 24) {
      toast.error("Pick between 1 and 24 months.");
      return;
    }
    setGranting(true);
    try {
      await axios.post(
        `${API}/admin/users/${grantingFor.user_id}/grant-pro`,
        { months, note: grantNote.trim() || null },
        { withCredentials: true },
      );
      toast.success(
        `Granted ${months} month${months === 1 ? "" : "s"} of Pro to ${grantingFor.email}.`,
      );
      setGrantingFor(null);
      setGrantNote("");
      await load();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Couldn't grant Pro — try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't grant.");
    } finally {
      setGranting(false);
    }
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return data.rows;
    const q = query.trim().toLowerCase();
    return data.rows.filter(
      (r) =>
        (r.email || "").toLowerCase().includes(q) ||
        (r.name || "").toLowerCase().includes(q) ||
        (r.note || "").toLowerCase().includes(q),
    );
  }, [data.rows, query]);

  return (
    <div className="bg-[#1F2329] border border-white/8 rounded-2xl overflow-hidden">
      {/* Section header with stats */}
      <div className="px-5 sm:px-6 py-5 border-b border-white/5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-[#9B6A6C]/20 border border-[#9B6A6C]/40 flex items-center justify-center shrink-0">
            <Users className="h-5 w-5 text-[#D5A8A4]" />
          </div>
          <div>
            <h2 className="text-white text-base font-medium leading-tight">
              Members
            </h2>
            <p className="text-white/55 text-xs mt-0.5">
              Everyone who's signed up · plus people you've personally invited.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs flex-wrap">
          <span className="text-white/65">
            <span className="text-white/90 font-medium" data-testid="members-count">
              {data.total_members}
            </span>{" "}
            members
          </span>
          {data.total_pro > 0 && (
            <span className="text-[#D5A8A4]">
              <span className="font-medium" data-testid="members-pro-count">
                {data.total_pro}
              </span>{" "}
              Pro
            </span>
          )}
          {data.pro_by_source?.paid > 0 && (
            <span
              className="text-[#86EFAC]/85"
              title="Real revenue (Stripe-paid)"
              data-testid="members-pro-paid-count"
            >
              · {data.pro_by_source.paid} paid
            </span>
          )}
          {data.pro_by_source?.gift_code > 0 && (
            <span className="text-[#F9A8D4]/85" data-testid="members-pro-gift-count">
              · {data.pro_by_source.gift_code} gift
            </span>
          )}
          {data.pro_by_source?.manual > 0 && (
            <span className="text-[#93C5FD]/85" data-testid="members-pro-manual-count">
              · {data.pro_by_source.manual} manual
            </span>
          )}
          {data.pro_by_source?.admin > 0 && (
            <span className="text-[#D4A14A]/85" data-testid="members-pro-admin-count">
              · {data.pro_by_source.admin} admin
            </span>
          )}
          {data.pro_by_source?.unknown > 0 && (
            <span className="text-white/55" data-testid="members-pro-unknown-count">
              · {data.pro_by_source.unknown} review
            </span>
          )}
          {data.total_invitees > 0 && (
            <span className="text-white/55">
              <span className="text-white/85 font-medium" data-testid="members-invitee-count">
                {data.total_invitees}
              </span>{" "}
              pending invites
            </span>
          )}
        </div>
      </div>

      {/* Mode toggle + invite forms */}
      <div className="px-5 sm:px-6 pt-5">
        <div className="flex items-center gap-2 mb-3" data-testid="invite-mode-toggle">
          <button
            type="button"
            onClick={() => setMode("single")}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              mode === "single"
                ? "bg-[#9B6A6C] text-white"
                : "bg-[#0F1115] text-white/60 hover:text-white border border-white/10"
            }`}
            data-testid="invite-mode-single"
          >
            <Plus className="h-3.5 w-3.5" />
            Invite one
          </button>
          <button
            type="button"
            onClick={() => setMode("bulk")}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              mode === "bulk"
                ? "bg-[#9B6A6C] text-white"
                : "bg-[#0F1115] text-white/60 hover:text-white border border-white/10"
            }`}
            data-testid="invite-mode-bulk"
          >
            <ListPlus className="h-3.5 w-3.5" />
            Paste a list
          </button>
        </div>

        {mode === "single" && (
          <form
            onSubmit={onAddSingle}
            className="bg-[#0F1115] border border-white/8 rounded-xl p-4"
            data-testid="admin-beta-add-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-[2fr_2fr_auto] gap-2">
              <input
                type="email"
                required
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="friend@example.com"
                className="bg-[#1F2329] border border-white/10 rounded-lg px-3.5 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#9B6A6C]/60 transition-colors"
                data-testid="admin-beta-add-email"
              />
              <input
                type="text"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Internal note (optional)"
                maxLength={200}
                className="bg-[#1F2329] border border-white/10 rounded-lg px-3.5 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#9B6A6C]/60 transition-colors"
                data-testid="admin-beta-add-note"
              />
              <button
                type="submit"
                disabled={adding || !newEmail.trim()}
                className="inline-flex items-center justify-center gap-2 bg-[#9B6A6C] hover:bg-[#7d5455] text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-beta-add-submit"
              >
                {adding ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : sendInvite ? (
                  <Send className="h-4 w-4" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {sendInvite ? "Send invite" : "Add"}
              </button>
            </div>
            <label className="flex items-center gap-2 mt-2.5 text-white/55 text-xs cursor-pointer select-none">
              <input
                type="checkbox"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
                className="h-3.5 w-3.5 rounded accent-[#9B6A6C]"
                data-testid="admin-beta-send-invite"
              />
              Send invite email now
            </label>
          </form>
        )}

        {mode === "bulk" && (
          <form
            onSubmit={onAddBulk}
            className="bg-[#0F1115] border border-white/8 rounded-xl p-4"
            data-testid="admin-beta-bulk-form"
          >
            <textarea
              value={bulkRaw}
              onChange={(e) => setBulkRaw(e.target.value)}
              placeholder={`alex@example.com\nsam@example.com\njordan@example.com`}
              rows={5}
              className="w-full bg-[#1F2329] border border-white/10 rounded-lg px-3.5 py-2.5 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#9B6A6C]/60 transition-colors font-mono"
              data-testid="admin-beta-bulk-textarea"
            />
            <div className="grid grid-cols-1 sm:grid-cols-[2fr_auto] gap-2 mt-2.5">
              <input
                type="text"
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
                placeholder="Internal note for all (optional)"
                maxLength={200}
                className="bg-[#1F2329] border border-white/10 rounded-lg px-3.5 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#9B6A6C]/60 transition-colors"
                data-testid="admin-beta-bulk-note"
              />
              <button
                type="submit"
                disabled={bulking || !bulkRaw.trim()}
                className="inline-flex items-center justify-center gap-2 bg-[#9B6A6C] hover:bg-[#7d5455] text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-beta-bulk-submit"
              >
                {bulking ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Send invites
              </button>
            </div>
            <label className="flex items-center gap-2 mt-2.5 text-white/55 text-xs cursor-pointer select-none">
              <input
                type="checkbox"
                checked={bulkSend}
                onChange={(e) => setBulkSend(e.target.checked)}
                className="h-3.5 w-3.5 rounded accent-[#9B6A6C]"
                data-testid="admin-beta-bulk-send-invite"
              />
              Send invite emails after adding
            </label>
          </form>
        )}
      </div>

      {/* Filter tabs — segment the table by Pro source */}
      <div className="px-5 sm:px-6 pt-4">
        <div
          className="flex items-center gap-1 flex-wrap"
          data-testid="members-filter-tabs"
        >
          <Filter className="h-3.5 w-3.5 text-white/40 mr-1" />
          {FILTER_TABS.map((t) => (
            <button
              key={t.key || "all"}
              type="button"
              onClick={() => setFilter(t.key)}
              className={`px-2.5 py-1 rounded-md text-[11px] uppercase tracking-wider font-medium transition-colors ${
                filter === t.key
                  ? "bg-[#9B6A6C] text-white"
                  : "bg-white/[0.04] text-white/55 hover:text-white hover:bg-white/[0.08] border border-white/8"
              }`}
              data-testid={`members-filter-${t.key || "all"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="px-5 sm:px-6 py-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/35" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by email, name, or note…"
            className="w-full bg-[#0F1115] border border-white/10 rounded-lg pl-9 pr-4 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#9B6A6C]/60 transition-colors"
            data-testid="admin-members-search"
          />
        </div>
      </div>

      {/* Table */}
      {error ? (
        <div className="m-5 flex items-center gap-2 text-[#FBA9A1] bg-[#5d2222]/30 border border-[#FBA9A1]/30 rounded-xl px-4 py-3">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-white/40" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="px-5 py-10 text-center text-white/45 text-sm">
          {query
            ? "No matches."
            : "No members yet — invite your first one above."}
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="admin-members-list">
          <table className="w-full text-sm">
            <thead className="bg-[#0F1115] text-white/55 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Member</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">
                  Plan
                </th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">
                  Converts to paid
                </th>
                <th className="text-left px-5 py-3 font-medium">Sign-in</th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">
                  Joined
                </th>
                <th className="text-left px-5 py-3 font-medium whitespace-nowrap">
                  Last active
                </th>
                <th className="text-right px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((r, i) => (
                <tr
                  key={`${r.kind}-${r.user_id || r.allowlist_id || i}`}
                  className="hover:bg-white/[0.02] transition-colors"
                  data-testid={`admin-members-row-${r.user_id || r.allowlist_id}`}
                >
                  <td className="px-5 py-3 text-white">
                    <div className="font-medium truncate max-w-[260px]">
                      {r.email || "—"}
                    </div>
                    {r.name && r.kind === "member" ? (
                      <div className="text-white/35 text-xs mt-0.5 truncate max-w-[260px]">
                        {r.name}
                      </div>
                    ) : r.note ? (
                      <div className="text-white/35 text-xs mt-0.5 italic truncate max-w-[260px]">
                        {r.note}
                      </div>
                    ) : null}
                    {/* Day One + Founding Member chips — inline under
                        the email so the founder sees them at-a-glance
                        in the audit/triage view. */}
                    {(r.is_founding_member || r.is_day_one) && (
                      <div className="mt-1.5">
                        <MemberBadges
                          isFoundingMember={r.is_founding_member}
                          isDayOne={r.is_day_one}
                        />
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {r.kind === "invitee" ? (
                      r.invited_at ? (
                        <span
                          className="inline-flex items-center gap-1.5 bg-[#3a2a08]/40 text-[#D4A14A] border border-[#D4A14A]/30 rounded-full px-2.5 py-1 text-xs font-medium"
                          data-testid="status-invited"
                        >
                          <Clock className="h-3 w-3" />
                          Invited
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1.5 bg-white/5 text-white/50 border border-white/10 rounded-full px-2.5 py-1 text-xs font-medium"
                          data-testid="status-allowlisted"
                        >
                          Allowlisted
                        </span>
                      )
                    ) : r.subscription_status === "trialing" ? (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border bg-[#3a2a08]/60 text-[#D4B483] border-[#D4B483]/40"
                        data-testid="status-trialing"
                      >
                        <Clock className="h-3 w-3" />
                        Trial
                        {r.subscription_cancel_at_period_end && (
                          <span className="opacity-70 border-l border-current/30 pl-1.5 ml-0.5">
                            canceling
                          </span>
                        )}
                      </span>
                    ) : r.is_pro ? (
                      (() => {
                        const meta =
                          PRO_SOURCE_META[r.pro_source] ||
                          PRO_SOURCE_META.unknown;
                        const IconC = meta.icon;
                        return (
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border ${meta.chipClass}`}
                            title={meta.explainer}
                            data-testid={`status-pro-${r.pro_source || "unknown"}`}
                          >
                            <Crown className="h-3 w-3" />
                            <span>Pro</span>
                            <span className="opacity-65 inline-flex items-center gap-1 border-l border-current/30 pl-1.5 ml-0.5">
                              <IconC className="h-3 w-3" />
                              {meta.label}
                            </span>
                          </span>
                        );
                      })()
                    ) : (
                      <span
                        className="inline-flex items-center gap-1.5 bg-[#1B3924]/60 text-[#84C99A] border border-[#84C99A]/30 rounded-full px-2.5 py-1 text-xs font-medium"
                        data-testid="status-member"
                      >
                        <Check className="h-3 w-3" />
                        Member
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-white/55 text-xs whitespace-nowrap">
                    {r.kind === "member" &&
                    (r.selected_package_id || r.pro_package_id) ? (
                      <div>
                        <div className="text-white/75">
                          {PLAN_LABEL[r.selected_package_id || r.pro_package_id] ||
                            r.selected_package_id ||
                            r.pro_package_id}
                        </div>
                        {r.subscription_status === "trialing" && (
                          <div className="text-[10px] text-[#D4B483] mt-0.5">
                            pre-subscribed
                          </div>
                        )}
                      </div>
                    ) : r.kind === "member" && r.pro_source === "gift_code" ? (
                      <span className="text-white/55">Gifted Pro</span>
                    ) : r.kind === "member" &&
                      ["manual", "admin"].includes(r.pro_source) ? (
                      <span className="text-white/55">Complimentary Pro</span>
                    ) : (
                      <div>
                        <div className="text-white/35">No paid plan</div>
                        <div className="text-[10px] text-white/25 mt-0.5">
                          nothing selected
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs whitespace-nowrap">
                    {(() => {
                      const conversion = trialConversionLabel(r);
                      if (!conversion) return <span className="text-white/25">—</span>;
                      return (
                        <div data-testid={`member-conversion-${r.user_id}`}>
                          <div className={conversion.className}>{conversion.primary}</div>
                          <div className="text-[10px] text-white/35 mt-0.5">
                            {conversion.secondary}
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-5 py-3 text-white/65 text-xs">
                    {r.kind === "invitee" ? (
                      <span className="text-white/30 italic">—</span>
                    ) : (
                      PROVIDER_LABEL[r.auth_provider] || r.auth_provider || "—"
                    )}
                  </td>
                  <td className="px-5 py-3 text-white/55 text-xs whitespace-nowrap">
                    {r.kind === "invitee"
                      ? r.invited_at
                        ? `Invited ${fmtRelative(r.invited_at)}`
                        : `Added ${fmtRelative(r.added_at)}`
                      : fmtDate(r.joined_at)}
                  </td>
                  <td className="px-5 py-3 text-white/55 text-xs whitespace-nowrap">
                    {fmtRelative(r.last_active_at)}
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {r.kind === "invitee" && (
                      <>
                        <button
                          type="button"
                          onClick={() => onResend(r)}
                          disabled={resendingId === r.allowlist_id}
                          className="inline-flex items-center gap-1 text-[#D5A8A4] hover:text-white hover:bg-white/[0.04] rounded-md px-2 py-1 transition-colors disabled:opacity-50 mr-1"
                          data-testid={`admin-members-resend-${r.allowlist_id}`}
                          title="Send invite email again"
                        >
                          {resendingId === r.allowlist_id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Mail className="h-3.5 w-3.5" />
                          )}
                          <span className="text-xs">
                            {r.invited_at ? "Resend" : "Send"}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => onRemoveInvitee(r)}
                          className="inline-flex items-center gap-1 text-white/40 hover:text-[#FBA9A1] hover:bg-white/[0.04] rounded-md px-2 py-1 transition-colors"
                          data-testid={`admin-members-remove-${r.allowlist_id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                    {r.kind === "member" && r.user_id && (
                      <button
                        type="button"
                        onClick={() => openGrant(r)}
                        className="inline-flex items-center gap-1.5 text-[#D5A8A4] hover:text-white hover:bg-white/[0.04] rounded-md px-2 py-1 transition-colors"
                        data-testid={`admin-members-grant-${r.user_id}`}
                        title={r.is_pro ? "Extend Pro" : "Grant Pro (comp)"}
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                        <span className="text-xs">
                          {r.is_pro ? "Extend" : "Grant Pro"}
                        </span>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Grant Pro modal ─────────────────────────────────────── */}
      {grantingFor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 bg-black/60 backdrop-blur-sm"
          onClick={closeGrant}
          data-testid="grant-pro-modal"
        >
          <div
            className="relative max-w-md w-full bg-[#1F2329] border border-white/10 rounded-2xl shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="shrink-0 h-10 w-10 rounded-full bg-[#28324A]/60 border border-[#93C5FD]/40 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-[#93C5FD]" />
              </div>
              <div>
                <h3
                  className="text-white text-base font-medium"
                  data-testid="grant-pro-title"
                >
                  {grantingFor.is_pro ? "Extend Pro" : "Grant Pro"} ·{" "}
                  <span className="text-white/60">{grantingFor.email}</span>
                </h3>
                <p className="text-xs text-white/55 mt-0.5">
                  Stacks on top of any existing Pro time. Tagged as{" "}
                  <code className="text-[#93C5FD]">manual</code> so it
                  doesn't get counted as revenue.
                </p>
              </div>
            </div>

            <form onSubmit={onSubmitGrant} className="space-y-3">
              <div>
                <label
                  className="text-xs text-white/55 mb-1.5 block uppercase tracking-wider"
                  htmlFor="grant-months"
                >
                  Months of Pro
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  {[1, 3, 6, 12].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setGrantMonths(n)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        Number(grantMonths) === n
                          ? "bg-[#9B6A6C] text-white"
                          : "bg-white/[0.04] text-white/65 hover:text-white border border-white/8"
                      }`}
                      data-testid={`grant-months-preset-${n}`}
                    >
                      {n}
                      {n === 12 ? "y" : "m"}
                    </button>
                  ))}
                  <input
                    id="grant-months"
                    type="number"
                    min={1}
                    max={24}
                    value={grantMonths}
                    onChange={(e) => setGrantMonths(e.target.value)}
                    className="w-20 bg-[#0F1115] border border-white/10 rounded-md px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#9B6A6C]/60"
                    data-testid="grant-months-input"
                  />
                </div>
              </div>

              <div>
                <label
                  className="text-xs text-white/55 mb-1.5 block uppercase tracking-wider"
                  htmlFor="grant-note"
                >
                  Note (optional)
                </label>
                <input
                  id="grant-note"
                  type="text"
                  value={grantNote}
                  onChange={(e) => setGrantNote(e.target.value)}
                  maxLength={200}
                  placeholder="e.g. Founding-member thank-you"
                  className="w-full bg-[#0F1115] border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#9B6A6C]/60"
                  data-testid="grant-note-input"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeGrant}
                  disabled={granting}
                  className="px-4 py-2 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/[0.04] transition-colors disabled:opacity-50"
                  data-testid="grant-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={granting}
                  className="inline-flex items-center gap-2 bg-[#9B6A6C] hover:bg-[#7d5455] text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
                  data-testid="grant-submit"
                >
                  {granting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  {granting ? "Granting…" : "Grant Pro"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersPanel;
